/**
 * 교회학교 소개 페이지
 */

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { MainLayout } from '../../../../shared/components';
import { theme } from '../../../../shared/styles/theme';
import { mediaQueries } from '../../../../shared/constants/breakpoints';
import sundaySchool1 from '../../../../assets/images/sunday_school_1.png';
import sundaySchool2 from '../../../../assets/images/sunday_school_2.jpg';
import sundaySchool3 from '../../../../assets/images/sunday_school_3.jpg';
import sundaySchool4 from '../../../../assets/images/sunday_school_4.jpg';
import sundaySchool5 from '../../../../assets/images/sunday_school_5.jpg';
import sundaySchool6 from '../../../../assets/images/sunday_school_6.jpg';

interface SundaySchoolInfo {
  title: string;
  description: string;
  vision: string;
  schedule: {
    worship: string;
    biblStudy: string;
  };
  activities: string[];
}

// 교회학교 정보 하드코딩
const SUNDAY_SCHOOL_INFO: SundaySchoolInfo = {
  title: "위례세움교회 교회학교",
  description: "위례세움교회 교회학교는 다음 세대를 하나님의 말씀으로 양육하고, 예수 그리스도의 제자로 세우는 곳입니다. 유치부부터 초등부까지 각 연령에 맞는 체계적인 교육을 통해 아이들이 신앙 안에서 건강하게 성장하도록 돕고 있습니다.",
  vision: "우리 아이들이 하나님의 사랑 안에서 건강하게 자라나며, 믿음의 다음 세대 리더로 세워지도록 돕습니다.",
  schedule: {
    worship: "매주 주일 오전 11:00",
    biblStudy: "매주 주일 오전 11:40"
  },
  activities: [
    "주일 예배 및 성경공부",
    "여름/겨울 성경학교",
    "어린이 체육대회",
    "위례소년소녀 합창단"
  ]
};

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 800px;
  height: 400px;
  margin: 0 auto ${theme.spacing.xl};
  position: relative;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    height: 250px;
  }
`;

const SchoolImage = styled.img<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => props.isActive ? 1 : 0};
  transition: opacity 1s ease-in-out;
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing.xxxl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  border-bottom: 3px solid ${theme.colors.primary};
  padding-bottom: ${theme.spacing.md};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xl};
  }
`;

const VisionBox = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  padding: ${theme.spacing.xxl};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg};
  }
`;

const VisionText = styled.p`
  font-size: ${theme.fontSizes.xl};
  line-height: 1.8;
  margin: 0;

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const ContentText = styled.p`
  font-size: ${theme.fontSizes.lg};
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};
  max-width: 600px;
  margin: ${theme.spacing.xl} auto 0;

  ${mediaQueries.maxMobile} {
    grid-template-columns: 1fr;
    max-width: 100%;
  }
`;

const InfoCard = styled.div`
  background-color: ${theme.colors.hover};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
`;

const InfoLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
`;

const InfoValue = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  font-weight: 700;
  margin: 0;
`;

const ActivityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ActivityItem = styled.li`
  font-size: ${theme.fontSizes.lg};
  line-height: 2;
  color: ${theme.colors.text};
  padding-left: ${theme.spacing.lg};
  position: relative;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.secondary};
    font-weight: bold;
  }

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

export const SundaySchoolIntro: React.FC = () => {
  const info = SUNDAY_SCHOOL_INFO;
  const images = [sundaySchool1, sundaySchool2, sundaySchool3, sundaySchool4, sundaySchool5, sundaySchool6];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 2000); // 2초마다 이미지 변경

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <MainLayout>
      <PageContainer>
        <PageTitle>{info.title}</PageTitle>

        <SliderContainer>
          {images.map((image, index) => (
            <SchoolImage
              key={index}
              src={image}
              alt={`위례세움교회 교회학교 ${index + 1}`}
              isActive={index === currentImageIndex}
            />
          ))}
        </SliderContainer>

        <Section>
          <SectionTitle>교회학교 비전</SectionTitle>
          <VisionBox>
            <VisionText>{info.vision}</VisionText>
          </VisionBox>
        </Section>

        <Section>
          <SectionTitle>교회학교 소개</SectionTitle>
          <ContentText>{info.description}</ContentText>
        </Section>

        <Section>
          <SectionTitle>예배 및 활동 시간</SectionTitle>
          <ScheduleGrid>
            <InfoCard>
              <InfoLabel>예배</InfoLabel>
              <InfoValue>{info.schedule.worship}</InfoValue>
            </InfoCard>
            <InfoCard>
              <InfoLabel>성경공부</InfoLabel>
              <InfoValue>{info.schedule.biblStudy}</InfoValue>
            </InfoCard>
          </ScheduleGrid>
        </Section>

        <Section>
          <SectionTitle>주요 활동</SectionTitle>
          <ActivityList>
            {info.activities.map((activity, index) => (
              <ActivityItem key={index}>{activity}</ActivityItem>
            ))}
          </ActivityList>
        </Section>
      </PageContainer>
    </MainLayout>
  );
};
